import React, { useState, useEffect } from 'react';
import { Card, BlockStack, InlineStack, Text, Button, TextField, Spinner } from '@shopify/polaris';
import { PlayIcon } from '@shopify/polaris-icons';
import { formatServiceLabel } from '../../utils/serviceLabels';

export function ManualSync({ 
  service, 
  onSync, 
  isLoading, 
  error,
  entityLabel,
  placeholder,
  notice
}) {
  const [syncIds, setSyncIds] = useState('');
  const [fieldError, setFieldError] = useState('');
  const serviceLabel = formatServiceLabel(service);

  // Clear syncIds and fieldError when service changes (tab switch)
  useEffect(() => {
    setSyncIds('');
    setFieldError('');
  }, [service]);

  // Debug: log the service prop

  const handleSync = () => {
    // Ensure we only send clean, serializable data
    const ids = syncIds ? syncIds.split(',').map(id => id.trim()).filter(id => id.length > 0) : [];
    if (!ids.length) {
      setFieldError('Enter at least one ID');
      return;
    }
    const cleanPayload = { entityIds: ids, ids, timestamp: new Date().toISOString() };
    
    // Debug: log the payload to ensure it's clean
    
    // Call onSync with only the clean payload, no additional context
    try {
      onSync(cleanPayload);
    } catch (error) {
    }
  };

  const handleClear = () => {
    setSyncIds('');
  };

  return (
    <Card sectioned>
      <BlockStack gap="loose">
        <div className="card-title-container">
          <Text variant="headingMd" fontWeight="bold" className="card-title-standard">
            Manual Sync - {serviceLabel}
          </Text>
        </div>
        
        <Text variant="bodyMd" color="subdued">
          Manually trigger synchronization for specific records.
        </Text>

        {notice && (
          <div className="info-message">
            <Text variant="bodyMd" color="subdued">
              {notice}
            </Text>
          </div>
        )}

        {error && (
          <div className="error-message">
            <Text variant="bodyMd" color="critical">
              Error: {error}
            </Text>
          </div>
        )}

        <div className="manual-sync-form">
          <TextField
            label={entityLabel || 'Record IDs (comma-separated)'}
            value={syncIds}
            onChange={(v) => { setSyncIds(v); if (fieldError) setFieldError(''); }}
            placeholder={placeholder || 'e.g., 123, 456, 789'}
            disabled={isLoading}
            error={fieldError}
          />

          <InlineStack gap="base">
            <Button
              icon={PlayIcon}
              primary
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSync();
              }}
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? 'Starting Sync...' : 'Start Sync'}
            </Button>
            
            <Button
              variant="tertiary"
              onClick={handleClear}
              disabled={isLoading}
            >
              Clear
            </Button>
          </InlineStack>
        </div>

        {isLoading && (
          <div className="sync-status">
            <InlineStack align="center" gap="base">
              <Spinner size="small" />
              <Text variant="bodyMd">
                Initializing manual sync...
              </Text>
            </InlineStack>
          </div>
        )}
      </BlockStack>
    </Card>
  );
}
